import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, Phone } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { AuthImage } from '@/components/auth-image';
import { form_error_message } from '@/lib/forms';
import { AdoptTabs } from '@/components/adopt-tabs';
import { use_my_adoptions } from '@/features/adoption-listing/hooks';
import { SPECIES_LABEL, age_label } from '@/features/adoption-listing/types';

export function MyAdoptionsPage() {
  const navigate = useNavigate();
  const q = use_my_adoptions();
  const rows = q.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Adopt a pet" description="Pets you've reached out to adopt." />
      <AdoptTabs />

      {q.isLoading ? (
        <Card><div className="flex items-center justify-center p-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div></Card>
      ) : q.error ? (
        <ErrorState data-testid="my-adoptions-error" title="Couldn't load your adoptions" description={form_error_message(q.error, 'Please try again.')} />
      ) : rows.length === 0 ? (
        <EmptyState data-testid="my-adoptions-empty" icon={Heart} title="No adoptions yet" description="Browse pets and reach out — they'll appear here once you do." />
      ) : (
        <Card>
          <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="my-adoptions">
            {rows.map((r) => {
              const l = r.listing;
              const primary = l.photos.find((p) => p.is_primary) ?? l.photos[0];
              return (
                <li key={r.id} className="flex flex-col gap-3 p-4" data-testid={`my-adoption-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={() => navigate(`/app/adopt/${l.id}`)} className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-input bg-primary/10 text-primary">
                      {primary ? <AuthImage path={`${primary.file_path}?variant=thumb`} alt="" className="h-full w-full object-cover" /> : <Heart className="h-6 w-6" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{SPECIES_LABEL[l.species]}{l.breed ? ` · ${l.breed}` : ''}</span>
                        <StatusPill tone={r.status === 'CONFIRMED' ? 'success' : 'warning'}>
                          {r.status === 'CONFIRMED' ? 'contact unlocked' : 'payment pending'}
                        </StatusPill>
                      </div>
                      <p className="text-sm text-muted-foreground">{[age_label(l), l.city].filter(Boolean).join(' · ')}</p>
                    </div>
                  </div>

                  {r.status === 'CONFIRMED' && l.contact ? (
                    <div className="rounded-input bg-secondary/40 p-3 text-sm" data-testid={`adoption-contact-${r.id}`}>
                      <p className="font-medium">{l.contact.contact_name}</p>
                      <a href={`tel:${l.contact.contact_phone}`} className="mt-0.5 flex items-center gap-1 text-primary hover:underline">
                        <Phone className="h-3.5 w-3.5" /> {l.contact.contact_phone}
                      </a>
                      {l.contact.address_line && <p className="mt-0.5 text-muted-foreground">{[l.contact.address_line, l.city, l.contact.pincode].filter(Boolean).join(', ')}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 rounded-input border-2 border-dashed border-brand-outline-variant/30 p-3 text-sm text-muted-foreground">
                      <span>Finish the payment to reveal contact.</span>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/app/adopt/${l.id}`)} data-testid={`resume-adoption-${r.id}`}>Continue</Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
